import React, { useState, useEffect } from 'react';
import { Menu, X, Facebook } from 'lucide-react';
import { Button } from '../ui/button';
import { navLinks, companyData } from '../../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOnSubpage, setIsOnSubpage] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    const checkSubpage = () => {
      const hash = window.location.hash.replace('#', '');
      const subpages = ['objekte', 'impressum', 'datenschutz'];
      setIsOnSubpage(subpages.includes(hash));
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('hashchange', checkSubpage);
    checkSubpage(); // Check on initial load
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('hashchange', checkSubpage);
    };
  }, []);

  // Determine if header should have solid background
  const shouldHaveSolidBg = isScrolled || isOnSubpage;

  const scrollToSection = (href) => {
    // Check if this is a page (like #objekte, #impressum, #datenschutz) or a section
    const pageLinks = ['#objekte', '#impressum', '#datenschutz'];
    
    if (pageLinks.includes(href)) {
      // Navigate to the page
      window.location.hash = href.replace('#', '');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (href === '#home') {
      // Go to home
      window.location.hash = '';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // First ensure we're on the home page
      if (window.location.hash && pageLinks.includes('#' + window.location.hash.replace('#', ''))) {
        window.location.hash = '';
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        shouldHaveSolidBg
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('#home');
            }}
            className="flex items-center gap-2"
          >
            <span className={`text-xl md:text-2xl font-bold tracking-tight transition-colors duration-300 ${
              shouldHaveSolidBg ? 'text-slate-900' : 'text-white'
            }`}>
              <span className="text-teal-600">ADMIN</span> Hausverwaltung
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.id}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.href);
                }}
                className={`text-sm font-medium transition-colors duration-300 hover:text-teal-600 ${
                  shouldHaveSolidBg ? 'text-slate-700' : 'text-white/90'
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://www.facebook.com/hausverwaltungadmin/"
              target="_blank"
              rel="noopener noreferrer"
              className={`transition-colors duration-300 hover:text-teal-600 ${
                shouldHaveSolidBg ? 'text-slate-600' : 'text-white/80'
              }`}
              aria-label="Facebook"
            >
              <Facebook className="w-5 h-5" />
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 transition-colors ${
              shouldHaveSolidBg ? 'text-slate-900' : 'text-white'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-white shadow-lg transition-all duration-300 overflow-hidden ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col p-6 gap-4">
          {navLinks.map((link) => (
            <a
              key={link.id}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(link.href);
              }}
              className="text-slate-700 font-medium py-2 hover:text-teal-600 transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://www.facebook.com/hausverwaltungadmin/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-slate-600 py-2 hover:text-teal-600 transition-colors"
          >
            <Facebook className="w-5 h-5" />
            <span>Facebook</span>
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Header;
